import type { ComputedRef, Ref } from 'vue'
import type { RevalidationItem } from '~/store-v2/revalidations/revalidationList.types'
import type { BrandOption, ItemEditState } from '~/ui-blocks/Revalidations/revalidationsShared.types'
import { RevalidationStatus } from '~/services-v2/financial-bff/__generated__/zeus'
import { revalidationListItemKey } from '~/store-v2/revalidations/revalidationListItemKey'
import {
  normalizeBrandKey,
  resolveBrandOptionForRawBrand,
  resolveInverterBrandForItem,
} from '~/ui-blocks/Revalidations/revalidations.constants'

const READ_ONLY_STATUSES = new Set([
  RevalidationStatus.WAITING_COMMISSIONING_RESULT,
  RevalidationStatus.APPROVED,
])

export interface UseRevalidationEditStatesOptions {
  flatItems: ComputedRef<RevalidationItem[]>
  brandOptions: BrandOption[]
  brandOtherValue: string
  protectedKeys?: Ref<Set<string>>
}

export function useRevalidationEditStates(options: UseRevalidationEditStatesOptions) {
  const { flatItems, brandOptions, brandOtherValue, protectedKeys } = options

  const itemEditStates = ref<Record<string, ItemEditState>>({})

  function createDefaultItemEditState(item: RevalidationItem): ItemEditState {
    const { brand: matchedBrand, customBrand } = resolveBrandOptionForRawBrand(
      resolveInverterBrandForItem(item),
      brandOptions,
      brandOtherValue,
    )
    const isReadOnly = READ_ONLY_STATUSES.has(item.status)
    const serials = isReadOnly
      ? (item.latest_submission ?? [])
          .map(s => s.serial_number?.trim())
          .filter((s): s is string => Boolean(s))
      : []
    return {
      brand: matchedBrand,
      customBrand,
      serials,
      invalidSerials: [],
      plantName: isReadOnly ? (item.plant_name?.trim() ?? '') : '',
    }
  }

  function getItemEditState(rowKey: string): ItemEditState {
    return itemEditStates.value[rowKey]!
  }

  function patchItemEdit(rowKey: string, patch: Partial<ItemEditState>) {
    const state = getItemEditState(rowKey)
    Object.assign(state, patch)
    if (patch.serials != null && state.invalidSerials.length) {
      const present = new Set(patch.serials)
      state.invalidSerials = state.invalidSerials.filter(s => present.has(s))
    }
  }

  function isItemFilled(rowKey: string, defaultPlantName: string): boolean {
    const state = itemEditStates.value[rowKey]
    if (!state) return false
    const hasSerial = state.serials.length > 0 && state.serials.some(s => s.trim() !== '')
    const hasPlantName = state.plantName.trim() !== '' && state.plantName.trim() !== defaultPlantName.trim()
    return hasSerial || hasPlantName
  }

  function deviceBrandSlugForRow(rowKey: string, item: RevalidationItem): string {
    const state = itemEditStates.value[rowKey]
    if (state?.brand?.value && state.brand.value !== brandOtherValue)
      return normalizeBrandKey(state.brand.value).replace(/-/g, '_')
    if (state?.customBrand?.trim())
      return normalizeBrandKey(state.customBrand.trim()).replace(/-/g, '_')
    return normalizeBrandKey(resolveInverterBrandForItem(item)).replace(/-/g, '_')
  }

  function removeEditStatesForKeys(keys: readonly string[]) {
    for (const k of keys)
      delete itemEditStates.value[k]
  }

  watch(
    flatItems,
    (items) => {
      const valid = new Set(items.map(revalidationListItemKey))
      for (const k of Object.keys(itemEditStates.value)) {
        if (!valid.has(k) && !protectedKeys?.value.has(k))
          delete itemEditStates.value[k]
      }
      for (const item of items) {
        const rowKey = revalidationListItemKey(item)
        if (!itemEditStates.value[rowKey])
          itemEditStates.value[rowKey] = createDefaultItemEditState(item)
      }
    },
    { flush: 'sync', immediate: true },
  )

  return {
    itemEditStates,
    getItemEditState,
    patchItemEdit,
    isItemFilled,
    deviceBrandSlugForRow,
    removeEditStatesForKeys,
  }
}
